import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { coursesAPI } from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { canAssignType } from '../utils/coursePermissions';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const TemplateDetails = () => {
  const { t } = useTranslation();
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [using, setUsing] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Poate user-ul curent să șteargă acest template?
  // - SUPER_ADMIN: orice (inclusiv cele oficiale)
  // - SSM_COMPANY_ADMIN / SSM_INSTRUCTOR: template-urile non-globale din firma SSM proprie
  //   (backend `canAccessCourse` validează prin creator.companyId === user.companyId)
  const canDelete = (() => {
    if (!course) return false;
    if (user?.role === 'SUPER_ADMIN') return true;
    if (['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role) && !course.isGlobal)
      return true;
    return false;
  })();

  // Boundary PSI/SSM la clonare: poate user-ul să cloneze ACEST tip de template?
  // Refolosim aceeași regulă ca la creare/atribuire (canAssignType). Ex: un instructor
  // doar-SSM (fără bifa PSI) vede template-urile SU dar NU le poate clona (read-only).
  // Backend-ul aplică același check — UI-ul doar reflectă vizual restricția.
  const canClone = (() => {
    if (!course) return false;
    return canAssignType(user, !!user?.managesDepartment, course.type);
  })();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await coursesAPI.delete(templateId);
      showToast(t('templateDetails.deletedToast'), 'success');
      navigate('/courses?tab=templates');
    } catch (err) {
      showToast(err.response?.data?.error || t('templateDetails.deleteError'), 'error');
      setDeleting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const load = async () => {
    setLoading(true);
    try {
      // Endpoint dedicat pentru template — include modulele, întrebările și final test.
      const res = await coursesAPI.getTemplate(templateId);
      setCourse(res.data);
      setCustomTitle(
        res.data.title?.replace(/^📋\s*\[Template\]\s*/, '').trim() +
          t('templateDetails.customSuffix')
      );
    } catch (err) {
      showToast(err.response?.data?.error || t('templateDetails.loadError'), 'error');
      navigate('/courses?tab=templates');
    } finally {
      setLoading(false);
    }
  };

  const confirmUse = async () => {
    if (!canClone) {
      showToast(t('templateDetails.noCloneRight'), 'error');
      return;
    }
    if (!customTitle.trim()) {
      showToast(t('templateDetails.enterTitle'), 'error');
      return;
    }
    setUsing(true);
    try {
      const res = await coursesAPI.clone(templateId, { title: customTitle.trim() });
      showToast(t('templateDetails.clonedToast'), 'success');
      // Redirect la pagina de edit detalii curs (nu content direct — user-ul poate
      // schimba întâi titlul/descrierea personalizat înainte de conținut).
      navigate(`/courses/${res.data.course.id}/edit`);
    } catch (err) {
      showToast(err.response?.data?.error || t('templateDetails.cloneError'), 'error');
    } finally {
      setUsing(false);
      setShowConfirm(false);
    }
  };

  if (loading) return <Spinner size="lg" label={t('templateDetails.loadingTemplate')} />;
  if (!course) return null;

  const totalQuestions = (course.modules || []).reduce((s, m) => s + (m.questions?.length || 0), 0);
  const finalTestQuestions = course.finalTest?.questions?.length || 0;

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('templateDetails.breadcrumbCourses'), href: '/courses' },
          { label: t('templateDetails.breadcrumbTemplateLibrary'), href: '/courses?tab=templates' },
          { label: course.title || 'Template' },
        ]}
      />

      {course.isGlobal && (
        <div className="p-4 rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 flex items-start gap-3">
          <svg
            className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          <div className="text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
              {t('templateDetails.legalWarningTitle')}
            </p>
            <p className="text-amber-800 dark:text-amber-300 text-xs leading-relaxed">
              <Trans i18nKey="templateDetails.legalWarningBody" components={{ b: <strong /> }} />
            </p>
          </div>
        </div>
      )}

      <Card>
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge color={course.type?.endsWith('_SU') ? 'orange' : 'blue'}>{course.type}</Badge>
              {course.isGlobal ? (
                <Badge color="purple">{t('templateDetails.badgeOfficial')}</Badge>
              ) : (
                <Badge color="green">{t('templateDetails.badgeYourCompany')}</Badge>
              )}
              <Badge color="gray">{t('templateDetails.badgeReadOnly')}</Badge>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {course.title}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{course.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('templateDetails.labelDuration')}
                </p>
                <p className="font-semibold">{course.duration ? `${course.duration} min` : '—'}</p>
              </div>
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('templateDetails.labelModules')}
                </p>
                <p className="font-semibold">{course.modules?.length || 0}</p>
              </div>
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('templateDetails.labelModuleQuestions')}
                </p>
                <p className="font-semibold">{totalQuestions}</p>
              </div>
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('templateDetails.labelFinalTest')}
                </p>
                <p className="font-semibold">
                  {finalTestQuestions > 0
                    ? t('templateDetails.questionsCount', { count: finalTestQuestions })
                    : t('templateDetails.none')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={() => setShowConfirm(true)}
              disabled={!canClone}
              title={!canClone ? t('templateDetails.noCloneRight') : undefined}
            >
              {t('templateDetails.useTemplate')}
            </Button>
            {!canClone && (
              <p className="text-xs text-amber-700 dark:text-amber-300 max-w-[200px] leading-snug">
                {t('templateDetails.readOnlyNotice', {
                  type: course.type,
                  badge: course.type?.endsWith('_SU')
                    ? t('templateDetails.badgePSI')
                    : t('templateDetails.badgeSSM'),
                })}
              </p>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="text-danger-600 dark:text-danger-300 hover:bg-danger-50 dark:hover:bg-danger-950/40"
                onClick={() => setShowDeleteConfirm(true)}
              >
                {t('templateDetails.deleteTemplate')}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/courses?tab=templates')}>
              {t('templateDetails.backToLibrary')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Module */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('templateDetails.sectionModules', { count: course.modules?.length || 0 })}
        </h2>
        {!course.modules || course.modules.length === 0 ? (
          <p className="text-sm text-slate-500 italic">{t('templateDetails.noModules')}</p>
        ) : (
          <div className="space-y-3">
            {course.modules.map((mod, i) => (
              <div
                key={mod.id}
                className="p-3 rounded-lg border border-slate-200 dark:border-slate-800"
              >
                <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  {i + 1}. {mod.title}
                </h3>
                {mod.content && (
                  <div
                    className="prose prose-sm max-w-none text-slate-600 dark:text-slate-400 mb-2"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(mod.content) }}
                  />
                )}
                {mod.questions?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      {t('templateDetails.knowledgeCheck', { count: mod.questions.length })}
                    </p>
                    <ul className="list-disc list-inside text-xs text-slate-600 dark:text-slate-400 space-y-0.5">
                      {mod.questions.map((q) => (
                        <li key={q.id}>{q.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Final test preview */}
      {course.finalTest && finalTestQuestions > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {t('templateDetails.finalTestHeader', {
              count: finalTestQuestions,
              score: course.finalTest.passingScore,
            })}
          </h2>
          <ul className="list-disc list-inside text-sm text-slate-600 dark:text-slate-400 space-y-1">
            {course.finalTest.questions.map((q) => (
              <li key={q.id}>{q.text}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Modal confirmare */}
      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('templateDetails.deleteConfirmTitle')}
        message={t('templateDetails.deleteConfirmMessage', { title: course.title })}
        confirmLabel={t('templateDetails.deleteConfirmLabel')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        loading={deleting}
      />

      {showConfirm && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !using && setShowConfirm(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('templateDetails.useTemplateHeading')}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              <Trans i18nKey="templateDetails.useTemplateBody" components={{ b: <strong /> }} />
            </p>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('templateDetails.newCourseTitleLabel')}
            </label>
            <input
              type="text"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface text-sm"
              placeholder={t('templateDetails.newCoursePlaceholder')}
              autoFocus
            />
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="ghost" onClick={() => setShowConfirm(false)} disabled={using}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={confirmUse} loading={using}>
                {using ? t('templateDetails.creating') : t('templateDetails.createAndEdit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateDetails;
